import anylogger from '@app/anylogger'
import { GoogleEvent } from '@app/googlecalendar'
import { PageReferenceData } from '@app/mdxsource'
import { ErrorView, Flex, FlexC } from '@app/muiplus'
import { formatDateTime, formatTime, NestedError, TypesafeBoolean, valueCompare } from '@app/utils'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { getUserPreferences } from 'src/services/getUserPreferences'
import { useWebEditorService } from 'src/services/WebEditor/WebEditorService'
import { getDateFromUnion } from './calendarUtils'
import { Card } from './Card'
import SendErrorReport from './SendErrorReport'

const log = anylogger('CalendarCards')

export interface CalendarPageLink {
	event: GoogleEvent
	pageData: PageReferenceData
}
interface CalendarCardsProps {
	days: number
}
export function CalendarCards(props: CalendarCardsProps) {
	let { days = 14 } = props
	// even though days is defined as a number, when it is passed as a prop to CalendarCards, it may be a string
	// and if it is a string, adding it to a number will concatenate the string, causing an incorrect value to addDays
	days = Number(days)

	let cardProps = useMemo(() => {
		return { picPos: 'top', picPercent: 50, height: '100%' }
	}, [])
	const [events, setEvents] = useState<Record<string, CalendarPageLink[]>>({})
	const [error, setError] = useState<any>(undefined)

	const { prefer24HourTime } = getUserPreferences()

	const svc = useWebEditorService()
	useEffect(() => {
		const startDate = new Date()
		startDate.setHours(0, 0, 0, 0)

		const endDate = new Date(startDate)
		endDate.setDate(endDate.getDate() + days)
		svc.getEvents(startDate.toISOString(), endDate.toISOString())
			.then((events) => {
				setError(undefined)
				function sort(a: CalendarPageLink, b: CalendarPageLink) {
					let startA = a.event.start.dateTime
					if (!startA) startA = a.event.start.date + 'T00:00:00'
					let startB = b.event.start.dateTime
					if (!startB) startB = b.event.start.date + 'T00:00:00'
					return valueCompare(startA, startB)
				}
				events.sort(sort)
				let dict: Record<string, CalendarPageLink[]> = {}
				for (let ii = 0; ii < events.length; ii++) {
					const event = events[ii]

					const key = formatDateTime(getDateFromUnion(event.event.start), 'yyyy-mm-dd')

					if (!dict[key]) dict[key] = []
					dict[key].push(event)
				}
				setEvents(dict)
			})
			.catch((err) => {
				// setError(err)
				setError(new NestedError('Error getting the upcoming events', err))
			})
	}, [days, svc])

	const getCards = useCallback(
		(events: CalendarPageLink[]) => {
			if (!events) return null
			return events
				.map((item, idx) => {
					const { event, pageData } = item

					let newPage = { ...pageData }

					newPage.title = `${formatTime(getDateFromUnion(event.start), prefer24HourTime)} - ${event.summary}`
					newPage.summary = event.description || pageData.summary

					return <Card key={idx} {...cardProps} pageData={newPage} mx="0.5em" />
				})
				.filter(TypesafeBoolean)
		},
		[cardProps, prefer24HourTime]
	)
	const getDayEvents = useCallback(() => {
		if (error) {
			return <ErrorView error={error} sendErrorReportButton=<SendErrorReport size="small" />></ErrorView>
		}
		return Object.keys(events).map((key) => {
			const dayEvents = events[key]
			const cards = getCards(dayEvents)

			return (
				<FlexC mx="auto" key={key} p="0.5em">
					<Flex mx="auto">
						<h2>{formatDateTime(key, 'wdl, mmm dd, yyyy')}</h2>
					</Flex>
					{cards}
				</FlexC>
			)
		})
	}, [error, events, getCards])

	return (
		<FlexC fill mx="auto">
			<Flex mx="auto">
				<h1>Upcoming Events</h1>
			</Flex>
			{getDayEvents()}
		</FlexC>
	)
}
